<template>
  <div id="cesium-container" ref="cesiumContainer">
    <cesium-display-options ref="cesiumDisplayOptionsList">
      <v-checkbox
        class="ma-0 pa-0"
        v-for="(option, index) in displayOptions"
        :key="index"
        :label="option.label"
        v-model="option.show"
        :dark="true"
        color="info"
        hide-details
        slot="displayOptions"
      />
    </cesium-display-options>
    <cesium-geometries-list
      ref="cesiumSatellitesList"
      :constellations="geometries"
      :tracesPrimitiveCollection="rotatingOrbitalPlanes"
      :satellitesDS="satellitesDS"
      :isAnalyticalDashboard="false"
    />
    <cesium-lighting-button ref="cesiumLightingButton" />
    <cesium-recorder ref="cesiumRecorder" />
  </div>
</template>

<script>
import __ from 'lodash'
import dataTools from '../../tools/data'
import { CesiumInitialization } from '../../components/ThreeD/CesiumInitialization'
import { GroundStation } from '../../components/ThreeD/GroundStation'
import { AnalyticalOrbit } from '../../components/ThreeD/AnalyticalOrbit'
import { PointOfInterest } from '../../components/ThreeD/PointOfInterest'
import CesiumConstellationsTools from '../../components/ThreeD/CesiumConstellationsTools'
import CesiumDisplayOptionsList from '@/components/ThreeD/CesiumDisplayOptionsList.vue'
import CesiumGeometriesList from '../../components/ThreeD/CesiumGeometriesList.vue'
import CesiumTools from '../../components/ThreeD/CesiumTools'
import CesiumRecorder from '../../components/ThreeD/CesiumRecorder.vue'
import CesiumLightingButton from '../../components/ThreeD/CesiumLightingButton.vue'
import constellationUtils from '../../mixins/constellationUtils'
import { mapGetters } from 'vuex'
import * as Cesium from 'cesium'

export default {
  name: 'simulation-cesium-viewer',
  mixins: [
    CesiumInitialization,
    constellationUtils,
    AnalyticalOrbit,
    GroundStation,
    PointOfInterest
  ],
  props: [
    'pointOfInterest',
    'tracesResults',
    'name',
    'initialGuessResults',
    'merged',
    'isoStartDate'
  ],
  components: {
    'cesium-recorder': CesiumRecorder,
    'cesium-lighting-button': CesiumLightingButton,
    'cesium-geometries-list': CesiumGeometriesList,
    'cesium-display-options': CesiumDisplayOptionsList
  },
  data() {
    return {
      tracesPCToShow: [],
      nbOfTraces: 1,
      satellitesData: null,
      satellitesDS: null,
      satellitesPositions: [],
      timeStep: 5,
      lastUpdated: null,
      geometries: [],
      rotatingOrbitalPlanes: null,
      displayOptions: {
        pointsOfInterest: {
          show: true,
          label: 'Points Of Interest'
        },
        groundStations: {
          show: true,
          label: 'Ground stations'
        },
        spacecraftLabels: {
          show: true,
          label: 'Spacecraft labels'
        },
        payloads: {
          show: false,
          label: 'Payloads'
        }
      }
    }
  },
  computed: {
    ...mapGetters({
      currentResultsLog: 'currentResultsLog',
      currentMission: 'currentMission'
    })
  },

  watch: {
    'displayOptions.spacecraftLabels.show': {
      handler(show) {
        for (let i = 0; i < this.nbOfTraces; i++) {
          this.satellitesDS.entities.values[i].label.show = show
        }
      }
    },
    'displayOptions.payloads.show': {
      handler(show) {
        for (let i = 0; i < this.nbOfTraces; i++) {
          this.satellitesDS.entities.values[i].cylinder.show = show
        }
      }
    },
    'displayOptions.pointsOfInterest.show': {
      handler(show) {
        this.pointsOfInterestPC.show = show
      }
    },
    'displayOptions.groundStations.show': {
      handler(show) {
        this.groundStationsPC.show = show
      }
    }
  },

  async mounted() {
    this.initializeViewer(true, true, true, false)
    this.$refs.cesiumSatellitesList.insertSatellitesListInToolbar(
      this.$refs.cesiumContainer
    )
    this.$refs.cesiumDisplayOptionsList.insertDisplayOptionsInToolbar(
      this.$refs.cesiumContainer
    )
    this.insertElementInToolbar(this.$refs.cesiumLightingButton.$el)
    this.insertElementInToolbar(this.$refs.cesiumRecorder.$el)
    this.deactivateControls()
    this.hideSceneModePicker()
    this.$refs.cesiumLightingButton.toggleLighting()

    const defaultRectangle = Cesium.Rectangle.fromDegrees(-180, -90, 180, 90)
    Cesium.Camera.DEFAULT_VIEW_RECTANGLE = defaultRectangle

    this.initPrimitiveCollections()

    const [startTime, stopTime] = this.getSimulationDates()
    this.lastUpdated = startTime

    await Cesium.Transforms.preloadIcrfFixed(
      new Cesium.TimeInterval({
        start: startTime,
        stop: stopTime,
        isStartIncluded: true,
        isStopIncluded: true
      })
    )

    let constellationForGroundStations = Object.values(
      this.currentResultsLog.results.constellationsVisibilityData
    )[0].constellation
    let groundStationsDeepCopy = JSON.parse(
      JSON.stringify(this.currentResultsLog.results.groundStations)
    )
    groundStationsDeepCopy.sort((a, b) => (a.name < b.name ? -1 : 1))

    this.addGroundStations(
      [constellationForGroundStations],
      groundStationsDeepCopy
    )
    this.formatEphemerides()
    this.geometries =
      this.currentMission.constellation.homogeneousConstellationGeometries

    this.colorsArray = CesiumTools.createColorsArray(
      this.geometries.length > 1
        ? this.geometries.length
        : this.geometries[0].numberOfPlanes
    )

    this.rotatingOrbitalPlanes = new Cesium.PrimitiveCollection()
    this.viewer.scene.primitives.add(this.rotatingOrbitalPlanes)

    this.geometries.sort((a, b) =>
      a.displayedName.replaceAll('/', ' ') <
      b.displayedName.replaceAll('/', ' ')
        ? -1
        : 1
    )
    this.addNumericalTracesWithoutTimeIntervals(this.satellitesPositions, {
      primitiveCollection: this.rotatingOrbitalPlanes,
      colorsArray: this.colorsArray
    })

    this.addPointsOfInterest(this.currentMission.isolatedPointsOfInterest)
    this.addSatellitesAndLinks(this.satellitesData)
    // this.viewer.clock.onTick.addEventListener(this.updateVisibleTraces)

    this.viewer.clock.onTick.addEventListener(this.computeIcrfToFixed)
    this.viewer.scene.postUpdate.addEventListener(this.makeEarthSpin)
    this.viewer.scene.morphComplete.addEventListener(this.onMorphComplete)
    this.setCesiumClock(startTime, stopTime, 30)
    this.computeIcrfToFixed(this.viewer.clock, true)
    this.viewer.scene.render(this.viewer.clock.currentTime)
  },

  methods: {
    updateVisibleTraces(clock) {
      const timePassed = Cesium.JulianDate.secondsDifference(
        clock.currentTime,
        this.lastUpdated
      )

      // if time passed > 1 second of real time (or if update is forced):
      if (
        Math.abs(timePassed) > 1 ||
        Cesium.JulianDate.equalsEpsilon(
          clock.currentTime,
          this.viewer.clock.startTime,
          1
        )
      ) {
        this.tracesPCToShow.forEach((tracePC) => {
          for (let i = 0; i < tracePC.length; i++) {
            tracePC.primitiveCollection.get(i).show = false
          }
          let planesToShow = tracePC.timeInterval.getValue(
            this.viewer.clock.currentTime
          )
          if (planesToShow && planesToShow.length) {
            planesToShow.forEach((planeIndex) => {
              tracePC.primitiveCollection.get(planeIndex).show = true
            })
          }
        })
        this.lastUpdated = clock.currentTime
      }
    },

    initPrimitiveCollections() {
      this.satellitesDS = new Cesium.CustomDataSource()
      this.viewer.dataSources.add(this.satellitesDS)
      this.initTracesPC()
      this.initGroundStationsPC()
      this.initPointsOfInterestPC()
    },

    formatEphemerides() {
      const ephemerides = Object.values(
        this.currentResultsLog.results.threedEphemerides
      )
      const ephemKeys = Object.keys(
        this.currentResultsLog.results.threedEphemerides
      )
      const fieldIndexes = this.currentResultsLog.results.threedFieldIndexes
      let geometriesData = []

      ephemerides.forEach((ephem, index) => {
        geometriesData.push(
          dataTools.convertSimulationData(ephem, fieldIndexes)
        )
        let label = ephemKeys[index]
        let subConstellationName = label.split('#/#')[0]
        subConstellationName = subConstellationName.includes('/')
          ? subConstellationName.replaceAll('/', '')
          : subConstellationName
        let formattedLabel = subConstellationName
        for (const [ind, word] of Object.entries(label.split('#/#'))) {
          if (ind > 0) {
            formattedLabel += '#/#' + word
          }
        }

        let name = formattedLabel.replace(/#\/#\//g, '-')
        name = name.replace(/\s/g, '_')
        name = name.replace('-', '/')
        geometriesData[index][0].formattedName = name
        geometriesData[index][0].exactName = label
      })
      geometriesData.sort((a, b) =>
        a[0].formattedName < b[0].formattedName ? -1 : 1
      )

      this.satellitesData = geometriesData
      let traces = []
      this.timeStep =
        geometriesData[0][1].simulationDuration -
        geometriesData[0][0].simulationDuration
      traces = {}
      geometriesData.forEach((geometry) => {
        let currentName = geometry[0].exactName.split('#/#')[0]
        let satName = geometry[0].formattedName.split('/')[1]
        if (!traces[currentName]) {
          traces[currentName] = []
        }
        let traceValues = []
        geometry.forEach((sampledData) => {
          traceValues.push([
            sampledData.threedCartesianX,
            sampledData.threedCartesianY,
            sampledData.threedCartesianZ
          ])
        })
        traces[currentName][satName] = traceValues
      })
      this.satellitesPositions = traces
      this.nbOfTraces = this.satellitesData.length
    },

    getSimulationDates() {
      let startDate = Cesium.JulianDate.fromIso8601(
        this.currentMission.simulationDate
      )
      let stopDate = new Cesium.JulianDate()
      Cesium.JulianDate.addSeconds(
        startDate,
        this.currentMission.simulationDuration,
        stopDate
      )
      return [startDate, stopDate]
    },

    addSatellitesAndLinks(satellitesData) {
      satellitesData.forEach((satellite, satIndex) => {
        const satName = satellite[0].formattedName
        const satLabel = satellite[0].exactName
        const geometry = satLabel.split('#/#/')[0]
        const currentPlaneNumber = parseInt(
          satName.match(/(?<=plane-)(.*)(?=-spacecraft)/)[0]
        )
        const geometryIndex = this.geometries.findIndex((currGeometry) =>
          currGeometry.displayedName.includes(geometry)
        )
        const color =
          this.geometries.length > 1
            ? this.colorsArray[geometryIndex]
            : this.colorsArray[currentPlaneNumber - 1]
        this.addSatelliteEntity({ satName, geometry, color })
        this.addSatelliteGroundStationsVisibilityLines(
          this.currentResultsLog.results.groundStations,
          geometry,
          satLabel,
          satName
        )
      })
    },

    getPositionsAndCreateSampledPositionsProperty(geometry, satName) {
      satName = satName.split('/')[1]
      const satPositions = CesiumConstellationsTools.createShiftedPosArray(
        this.satellitesPositions[geometry][satName],
        0,
        this.timeStep
      )
      const sampledPositionProperty =
        CesiumConstellationsTools.createSampledPositionProperty(
          satPositions,
          this.getSimulationDates()[0],
          Cesium.ReferenceFrame.INERTIAL
        )
      return sampledPositionProperty
    },

    computeOrbitalPeriod(geometry, satName) {
      satName = satName.split('/')[1]
      const firstSatPos = Cesium.Cartesian3.unpack(
        this.satellitesPositions[geometry][satName][0]
      )
      const orbitalPeriod = CesiumConstellationsTools.getOrbitalPeriodFromSMA(
        Cesium.Cartesian3.magnitude(firstSatPos)
      )
      return orbitalPeriod
    },

    addSatelliteEntity(
      satellite = {
        satName: '',
        geometry: '',
        color: Cesium.Color.WHITE
      }
    ) {
      const { satName, geometry, color } = satellite
      // create a Cesium entity with only a time dynamic point
      let satEntity = this.initSatEntity(geometry, satName, color)
      // adds path primitive to the satellite (the continuously drawing orbit)
      /* satEntity.path = this.createSatPathGraphics(geometry, satName, color) */
      // adds a cylinder primitive to represent the conic payload
      satEntity.cylinder = this.createSatCylinderGraphics(geometry, color)
      this.satellitesDS.entities.add(satEntity)
    },

    initSatEntity(geometry, satName, color) {
      const sampledPositions =
        this.getPositionsAndCreateSampledPositionsProperty(geometry, satName)

      let satLabelText = satName.replace('plane', 'P')
      satLabelText = satName.replace('spacecraft', 'SC')

      return new Cesium.Entity({
        id: satName,
        description: satName,
        position: sampledPositions,
        show: true,
        point: {
          pixelSize: 8,
          color: color,
          outlineColor: Cesium.Color.BLACK,
          outlineWidth: 1
        },
        label: {
          text: satLabelText,
          font: '16px Helvetica',
          fillColor: Cesium.Color.WHITE,
          outlineColor: Cesium.Color.BLACK,
          outlineWidth: 2,
          pixelOffset: new Cesium.Cartesian2(0, 20),
          style: Cesium.LabelStyle.FILL_AND_OUTLINE,
          distanceDisplayCondition: new Cesium.DistanceDisplayCondition(
            5e4,
            3e7
          )
        }
      })
    },

    createSatPathGraphics(geometry, satName, color) {
      const orbitalPeriod = this.computeOrbitalPeriod(geometry, satName)

      return new Cesium.PathGraphics({
        show: false,
        leadTime: 0,
        trailTime: orbitalPeriod / 10,
        width: 1,
        resolution: orbitalPeriod,
        material: new Cesium.ColorMaterialProperty(color)
      })
    },

    createSatCylinderGraphics(geometry, color) {
      const altitude =
        this.currentResultsLog.results.constellationsVisibilityData[geometry]
          .constellation.plane.altitude
      const fov =
        this.currentResultsLog.results.constellationsVisibilityData[geometry]
          .constellation.payload.fieldOfView
          .semiViewAngleAlongTrackOperatingAltitude

      return new Cesium.CylinderGraphics({
        show: this.displayOptions.payloads.show,
        material: new Cesium.ColorMaterialProperty(
          Cesium.Color.fromAlpha(color, 0.3)
        ),
        length: altitude,
        topRadius: 0,
        bottomRadius: altitude * Math.tan(fov),
        height: altitude,
        heightReference: Cesium.HeightReference.CLAMP_TO_GROUND
      })
    },

    addSatelliteGroundStationsVisibilityLines(
      groundStations,
      geometry,
      satLabel,
      satName
    ) {
      groundStations.forEach((groundStation) => {
        let visibilityIntervalCollection =
          this.createVisibilityIntervalCollection(
            geometry,
            groundStation.name,
            satLabel
          )
        if (visibilityIntervalCollection) {
          this.viewer.entities.add(
            new Cesium.Entity({
              id: `${groundStation.name}/${satName}`,
              availability: visibilityIntervalCollection,
              polyline: {
                arcType: Cesium.ArcType.NONE,
                positions: this.getPolylineReferencePositions(
                  satName,
                  groundStation
                ),
                material: this.getPolylineMaterialProperty(satName),
                show: new Cesium.CallbackProperty(
                  () =>
                    this.displayOptions.groundStations.show &&
                    this.satellitesDS.entities.getById(satName).show,
                  false
                ),
                width: 4
              }
            })
          )
        }
      })
    },

    getPolylineReferencePositions(satName, groundStation) {
      return new Cesium.PositionPropertyArray([
        new Cesium.ConstantPositionProperty(
          Cesium.Cartesian3.fromRadians(
            groundStation.longitude,
            groundStation.latitude,
            groundStation.groundStationAltitude
          )
        ),
        new Cesium.ReferenceProperty(this.satellitesDS.entities, satName, [
          'position'
        ])
      ])
    },

    getPolylineMaterialProperty(satName) {
      return new Cesium.PolylineDashMaterialProperty({
        color: Cesium.ReferenceProperty.fromString(
          this.satellitesDS.entities,
          `${satName}#point.color`
        ),
        dashPattern: parseInt('1010101010101010', 2),
        dashLength: 32
      })
    },

    createVisibilityIntervalCollection(
      geometry,
      groundStationName,
      satelliteName
    ) {
      const visibilityTimes =
        this.currentResultsLog.results.constellationsVisibilityData[geometry]
          .groundStationsVisibilityIntervals[groundStationName]
          .stationVisibilityTimeline[satelliteName]
      if (!visibilityTimes || !visibilityTimes.length) {
        return null
      } else {
        const missionStartDate = this.getSimulationDates()[0]
        let intervalStart = new Cesium.JulianDate()
        let intervalStop = new Cesium.JulianDate()
        const timeIntervalCollection = new Cesium.TimeIntervalCollection()
        visibilityTimes.forEach((visibilityTime) => {
          Cesium.JulianDate.addSeconds(
            missionStartDate,
            visibilityTime.start,
            intervalStart
          )
          Cesium.JulianDate.addSeconds(
            missionStartDate,
            visibilityTime.end,
            intervalStop
          )
          timeIntervalCollection.addInterval(
            new Cesium.TimeInterval({
              start: intervalStart,
              stop: intervalStop
            })
          )
        })
        return timeIntervalCollection
      }
    }
  }
}
</script>

<style scoped>
#cesium-container {
  z-index: 2;
}
</style>
